import axios from 'axios'
import { agentRefusalFrom } from './aiAccess'

// Must match goapi/pkg/prompts/table_analysis.go's PERSONA_EXECUTIVE_SUMMARIZER
// name — selected by name rather than list position so the default doesn't
// depend on the library's ordering. Executive Summarizer, not Table
// Analyst: it is what every other AI surface defaults to (dashboard insights,
// the Gen AI wizard, chat), and Matt's rule for demos (11 Sep, #1347 item 8):
// "always use executive summarizer, not table analyst".
export const DEFAULT_PERSONA_NAME = 'Executive Summarizer'

export interface CreateAgentSessionBody {
  job: string
  jobs: string[]
  persona: string
  /** The model the session opens with; empty lets the server use its default. */
  model: string
  /** A profile the host knows by id; `chief-of-staff` for a Chief of Staff chat (Managers only). Absent for a Normal chat. */
  profile?: string
}

/** A failed create: `code` is set only for an AI access refusal (#1537 PR3), which the caller draws as a refusal rather than a plain error. */
export type AgentSessionError = Error & { code?: string }

/**
 * Create an agent session (POST /agentkit/agent/:customer/session) and return
 * its id. The start screen and the Alerts page both open chats this way.
 */
export async function createAgentSession(customer: string, body: CreateAgentSessionBody): Promise<string> {
  const token = (axios.defaults.headers.common['Authorization'] as string | undefined)?.replace(/^Bearer\s+/, '') ?? ''
  const resp = await fetch(`/api/v1/agentkit/agent/${customer}/session`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  })
  if (!resp.ok) {
    const text = await resp.text()
    let data: unknown
    try { data = JSON.parse(text) } catch { /* not JSON: the raw text below */ }
    const refusal = agentRefusalFrom(resp.status, data)
    const err: AgentSessionError = new Error(refusal?.text ?? `Failed to create session (${resp.status}): ${text}`)
    if (refusal) err.code = refusal.code
    throw err
  }
  const data = await resp.json() as { id: string }
  return data.id
}
